import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/layout/Layout';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
    MapPin, 
    Plus, 
    Pencil, 
    Trash2, 
    Building2,
    Truck,
    CheckCircle,
    Loader2,
    Star
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AddressesPage = () => {
    const { token, user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [addresses, setAddresses] = useState([]);
    const [billingAddress, setBillingAddress] = useState(null);
    
    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [editingAddress, setEditingAddress] = useState(null);
    const [addressForm, setAddressForm] = useState({
        name: '',
        street: '',
        street2: '',
        postal_code: '',
        city: '',
        country: 'FR',
        phone: '',
        is_default: false
    });

    const countries = [
        { code: 'FR', name: 'France' },
        { code: 'BE', name: 'Belgique' },
        { code: 'DE', name: 'Allemagne' },
        { code: 'ES', name: 'Espagne' },
        { code: 'IT', name: 'Italie' },
        { code: 'NL', name: 'Pays-Bas' },
        { code: 'PT', name: 'Portugal' },
        { code: 'CH', name: 'Suisse' },
        { code: 'LU', name: 'Luxembourg' },
        { code: 'GB', name: 'Royaume-Uni' }
    ];

    useEffect(() => {
        fetchAddresses();
    }, []);

    const fetchAddresses = async () => {
        try {
            const response = await axios.get(`${API_URL}/user/addresses`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAddresses(response.data.shipping_addresses || []);
            setBillingAddress(response.data.billing_address || null);
        } catch (error) {
            console.error('Fetch addresses error:', error);
            toast.error('Erreur lors du chargement des adresses');
        } finally {
            setLoading(false);
        }
    };

    const openAddModal = () => {
        setEditingAddress(null);
        setAddressForm({
            name: '',
            street: '',
            street2: '',
            postal_code: '',
            city: '',
            country: 'FR',
            phone: '',
            is_default: addresses.length === 0 // First address is default
        });
        setModalOpen(true);
    };

    const openEditModal = (address) => {
        setEditingAddress(address);
        setAddressForm({
            name: address.name || '',
            street: address.street || '',
            street2: address.street2 || '',
            postal_code: address.postal_code || '',
            city: address.city || '',
            country: address.country || 'FR',
            phone: address.phone || '',
            is_default: address.is_default || false
        });
        setModalOpen(true);
    };

    const handleSave = async () => {
        if (!addressForm.street || !addressForm.postal_code || !addressForm.city) {
            toast.error('Veuillez remplir les champs obligatoires');
            return;
        }

        setSaving(true);
        try {
            if (editingAddress) {
                // Update existing address
                await axios.put(
                    `${API_URL}/user/addresses/${editingAddress.id}`,
                    addressForm,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success('Adresse mise à jour');
            } else {
                // Create new address
                await axios.post(
                    `${API_URL}/user/addresses`,
                    addressForm,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success('Adresse ajoutée');
            }
            
            setModalOpen(false);
            fetchAddresses();
        } catch (error) {
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (addressId) => {
        if (!window.confirm('Supprimer cette adresse ?')) return;
        
        try {
            await axios.delete(`${API_URL}/user/addresses/${addressId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Adresse supprimée');
            fetchAddresses();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const setAsDefault = async (addressId) => {
        try {
            await axios.put(
                `${API_URL}/user/addresses/${addressId}/default`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Adresse par défaut mise à jour');
            fetchAddresses();
        } catch (error) {
            toast.error('Erreur lors de la mise à jour');
        }
    };

    const formatAddress = (addr) => {
        if (!addr) return '-';
        const parts = [addr.street, addr.street2, `${addr.postal_code} ${addr.city}`, countries.find(c => c.code === addr.country)?.name].filter(Boolean);
        return parts.join(', ');
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
            <Footer />
        </Layout>
        );
    }

    return (
        <Layout>
            <div className="max-w-4xl mx-auto px-6 py-8">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
                            Mes adresses
                        </h1>
                        <p className="text-slate-600 mt-1">
                            Gérez vos adresses de facturation et de livraison
                        </p>
                    </div>
                </div>

                <div className="space-y-6">
                    {/* Billing Address */}
                    <Card className="border-slate-200">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-lg">
                                <Building2 className="w-5 h-5 text-blue-600" />
                                Adresse de facturation
                            </CardTitle>
                            <CardDescription>
                                Adresse utilisée pour vos factures
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {billingAddress ? (
                                <div className="p-4 bg-slate-50 rounded-lg">
                                    <p className="font-medium text-slate-900">{user?.company_name}</p>
                                    <p className="text-slate-600">{billingAddress.street}</p>
                                    {billingAddress.street2 && <p className="text-slate-600">{billingAddress.street2}</p>}
                                    <p className="text-slate-600">{billingAddress.postal_code} {billingAddress.city}</p>
                                    <p className="text-slate-600">{countries.find(c => c.code === billingAddress.country)?.name || billingAddress.country}</p>
                                </div>
                            ) : (
                                <p className="text-slate-500 italic">Aucune adresse de facturation définie</p>
                            )}
                            <p className="text-xs text-slate-500 mt-3">
                                Pour modifier votre adresse de facturation, contactez le support.
                            </p>
                        </CardContent>
                    </Card>

                    {/* Shipping Addresses */}
                    <Card className="border-slate-200">
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className="flex items-center gap-2 text-lg">
                                        <Truck className="w-5 h-5 text-indigo-600" />
                                        Adresses de livraison
                                    </CardTitle>
                                    <CardDescription>
                                        Gérez vos adresses de livraison pour vos commandes
                                    </CardDescription>
                                </div>
                                <Button 
                                    onClick={openAddModal}
                                    className="bg-blue-600 hover:bg-blue-700"
                                    data-testid="add-address-btn"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Ajouter
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {addresses.length === 0 ? (
                                <div className="text-center py-8">
                                    <MapPin className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                                    <p className="text-slate-600">Aucune adresse de livraison</p>
                                    <p className="text-sm text-slate-500 mt-1">
                                        Ajoutez une adresse pour faciliter vos commandes
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {addresses.map((address) => (
                                        <div 
                                            key={address.id}
                                            className={`p-4 rounded-lg border transition-colors ${
                                                address.is_default 
                                                    ? 'border-blue-300 bg-blue-50' 
                                                    : 'border-slate-200 bg-white hover:border-slate-300'
                                            }`}
                                            data-testid={`address-${address.id}`}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <div className="flex items-center gap-2 mb-1">
                                                        {address.name && (
                                                            <span className="font-medium text-slate-900">{address.name}</span>
                                                        )}
                                                        {address.is_default && (
                                                            <Badge className="bg-blue-100 text-blue-700">
                                                                <Star className="w-3 h-3 mr-1" />
                                                                Par défaut
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-slate-600 text-sm">{address.street}</p>
                                                    {address.street2 && <p className="text-slate-600 text-sm">{address.street2}</p>}
                                                    <p className="text-slate-600 text-sm">{address.postal_code} {address.city}</p>
                                                    <p className="text-slate-500 text-sm">{countries.find(c => c.code === address.country)?.name}</p>
                                                    {address.phone && (
                                                        <p className="text-slate-500 text-sm mt-1">Tél: {address.phone}</p>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {!address.is_default && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => setAsDefault(address.id)}
                                                            className="text-slate-500 hover:text-blue-600"
                                                            title="Définir par défaut"
                                                        >
                                                            <CheckCircle className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => openEditModal(address)}
                                                        className="text-slate-500 hover:text-slate-700"
                                                    >
                                                        <Pencil className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDelete(address.id)}
                                                        className="text-slate-500 hover:text-red-600"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Add/Edit Address Modal */}
            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <MapPin className="w-5 h-5 text-indigo-600" />
                            {editingAddress ? 'Modifier l\'adresse' : 'Nouvelle adresse de livraison'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Nom / Libellé</Label>
                            <Input
                                value={addressForm.name}
                                onChange={(e) => setAddressForm(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="Ex: Bureau, Entrepôt..."
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Adresse *</Label>
                            <Input
                                value={addressForm.street}
                                onChange={(e) => setAddressForm(prev => ({ ...prev, street: e.target.value }))}
                                placeholder="123 rue de la Livraison"
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Complément</Label>
                            <Input
                                value={addressForm.street2}
                                onChange={(e) => setAddressForm(prev => ({ ...prev, street2: e.target.value }))}
                                placeholder="Bâtiment, étage..."
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label>Code postal *</Label>
                                <Input
                                    value={addressForm.postal_code}
                                    onChange={(e) => setAddressForm(prev => ({ ...prev, postal_code: e.target.value }))}
                                    placeholder="75001"
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Ville *</Label>
                                <Input
                                    value={addressForm.city}
                                    onChange={(e) => setAddressForm(prev => ({ ...prev, city: e.target.value }))}
                                    placeholder="Paris"
                                    required
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Pays</Label>
                            <Select
                                value={addressForm.country}
                                onValueChange={(value) => setAddressForm(prev => ({ ...prev, country: value }))}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {countries.map((country) => (
                                        <SelectItem key={country.code} value={country.code}>
                                            {country.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Téléphone</Label>
                            <Input
                                value={addressForm.phone}
                                onChange={(e) => setAddressForm(prev => ({ ...prev, phone: e.target.value }))}
                                placeholder="+33 6 00 00 00 00"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setModalOpen(false)}>
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleSave}
                            disabled={saving}
                            className="bg-blue-600 hover:bg-blue-700"
                        >
                            {saving ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : null}
                            {editingAddress ? 'Enregistrer' : 'Ajouter'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        <Footer />
        </Layout>
    );
};

export default AddressesPage;
